
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Sparkles, Heart, MessageCircle, Clock, MoreHorizontal } from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { useSwipeable } from 'react-swipeable';
import AbielAvatar from '@/components/ai-prayer/ai-assistant/components/AbielAvatar';
import { usePrayerChoice } from '@/contexts/PrayerChoiceContext';
import { logger, criticalLogger } from '@/utils/logger';

interface UniversalPrayerEntryProps {
  unreadCount?: number;
}

export default function UniversalPrayerEntry({
  unreadCount = 0
}: UniversalPrayerEntryProps) {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showQuickPreview, setShowQuickPreview] = useState(false);
  const [position, setPosition] = useState({ top: '50%', right: 16, transform: 'translateY(-50%)' });
  const [isVisible, setIsVisible] = useState(true);
  const { openCompanion } = usePrayerChoice();

  // Smart positioning based on viewport and content
  useEffect(() => {
    const updatePosition = () => {
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      
      // Avoid common UI elements
      const safeZones = {
        top: 80, // Header space
        bottom: 80, // Bottom navigation
        left: 20,
        right: 20
      };

      // Check if current position conflicts with page content
      const shouldRepositionRight = viewport.width < 768; // Mobile
      const shouldRepositionBottom = viewport.height < 600; // Short screens

      setPosition({
        top: '50%',
        right: shouldRepositionRight ? safeZones.right : 20,
        transform: 'translateY(-50%)'
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  // Swipe gestures for mobile
  const swipeHandlers = useSwipeable({
    onSwipedRight: () => handleClose(),
    onSwipedUp: () => openCompanion('cards'),
    onSwipedDown: () => setShowQuickPreview(!showQuickPreview),
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleHideUntilNextVisit = () => {
    sessionStorage.setItem('prayerChoiceHidden', 'true');
    setIsVisible(false);
  };

  const handleDisableForSite = () => {
    localStorage.setItem('prayerChoiceDisabled', 'true');
    setIsVisible(false);
  };

  const handleDisableGlobally = () => {
    localStorage.setItem('prayerChoiceDisabledGlobally', 'true');
    setIsVisible(false);
  };

  const getTimeBasedMessage = () => {
    const hour = new Date().getHours();
    if (hour < 10) return "Good morning! Need a prayer to start your day?";
    if (hour < 17) return "How can I pray with you this afternoon?";
    return "Good evening—let me pray with you tonight.";
  };

  const handleCompanionOpen = async () => {
    logger.log('🎯 Universal entry: Button clicked - Opening companion');
    logger.log('🎯 openCompanion function:', typeof openCompanion);
    
    try {
      await openCompanion('cards');
      logger.log('🎯 Universal entry: openCompanion completed successfully');
    } catch (error) {
      criticalLogger.error('🎯 Universal entry: Error opening companion:', error);
    }
  };

  // Check if should be hidden
  const isSessionHidden = sessionStorage.getItem('prayerChoiceHidden') === 'true';
  const isDisabled = localStorage.getItem('prayerChoiceDisabled') === 'true';
  const isDisabledGlobally = localStorage.getItem('prayerChoiceDisabledGlobally') === 'true';
  
  const shouldHide = !isVisible || isSessionHidden || isDisabled || isDisabledGlobally;
  
  if (shouldHide) {
    return null;
  }

  console.log('UniversalPrayerEntry: Rendering universal prayer entry at position:', position);

  return (
    <div 
      className="fixed z-float animate-slide-in-bottom"
      style={{ top: position.top, right: position.right, transform: position.transform }}
      {...swipeHandlers}
    >
      <div className="relative group">
        {/* Quick Preview on Hover */}
        {(showQuickPreview || isHovered) && (
          <div className="absolute bottom-full right-0 mb-4 w-64 bg-white/95 backdrop-blur-xl border border-gold/20 rounded-lg shadow-xl animate-fade-in p-4">
            <div className="flex items-center gap-2 mb-2">
              <AbielAvatar size="sm" /> 
              <span className="text-sm font-semibold text-prayer-primary">
                "Hi, I'm Abiel—your AI prayer guide"
              </span>
            </div>
            <div className="text-xs text-prayer-primary/70 mb-3">
              {getTimeBasedMessage()}
            </div>
            
            {/* Quick Action Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => openCompanion('chat')}
                size="sm"
                className="text-xs bg-gradient-to-r from-gold to-gold/90 hover:from-gold/90 hover:to-gold text-white"
              >
                <Heart className="h-3 w-3 mr-1" />
                Need Prayer
              </Button>
              <Button
                onClick={() => openCompanion('cards')}
                size="sm"
                variant="outline"
                className="text-xs border-gold/20 hover:border-gold/40 hover:bg-gold/5"
              >
                <MessageCircle className="h-3 w-3 mr-1" />
                Explore
              </Button>
            </div>
            
            {/* Triangle pointer */}
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white/95"></div>
          </div>
        )}

        {/* Enhanced Circular Avatar Button */}
        <button
          onClick={handleCompanionOpen}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="relative h-16 w-16 rounded-full bg-gradient-to-br from-gold/30 via-gold/20 to-gold/10 border-2 border-gold/40 hover:border-gold/60 transition-all duration-300 hover:scale-110 shadow-xl hover:shadow-2xl overflow-hidden backdrop-blur-sm cursor-pointer"
          aria-label="Chat with Abiel"
          title="Chat with Abiel"
        >
          {/* Animated background effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-gold/20 to-transparent rounded-full animate-pulse"></div>
          <div className="absolute inset-1 bg-gradient-to-br from-white/10 to-transparent rounded-full animate-pulse delay-300"></div>
          
          {/* Avatar with enhanced styling */}
          <div className="relative z-10 w-full h-full p-1">
            <AbielAvatar size="md" className="rounded-full w-full h-full shadow-inner" />
          </div>
          
          {/* Breathing glow effect */}
          <div className="absolute inset-0 rounded-full bg-gold/20 blur-xl animate-pulse opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
          
          {/* Sparkle indicator */}
          <div className={`absolute -top-1 -right-1 transition-all duration-300 ${isHovered ? 'scale-125' : 'scale-100'}`}>
            <Sparkles className="h-4 w-4 text-gold animate-pulse" />
          </div>

          {/* Notification badge */}
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -left-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs border-2 border-white animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </button>

        {/* Status indicator tooltip */}
        {isHovered && !showQuickPreview && (
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-prayer-primary text-white text-xs rounded-lg shadow-lg whitespace-nowrap animate-fade-in">
            <div className="flex items-center gap-1">
              <AbielAvatar size="xs" />
              Abiel is listening…
            </div>
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-prayer-primary"></div>
          </div>
        )}

        {/* Quick close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-gradient-to-br from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110"
          title="Close temporarily"
        >
          <X className="h-3 w-3" />
        </Button>

        {/* Options dropdown */}
        <DropdownMenu open={showContextMenu} onOpenChange={setShowContextMenu}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute -top-2 -left-2 h-7 w-7 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-110"
              title="More options"
            >
              <MoreHorizontal className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="start" 
            className="w-56 bg-white/95 backdrop-blur-xl border-gold/20 shadow-xl"
          >
            <DropdownMenuItem 
              onClick={handleHideUntilNextVisit}
              className="text-prayer-primary hover:bg-gold/10 transition-colors duration-200"
            >
              Hide until next visit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleDisableForSite}
              className="text-prayer-primary hover:bg-gold/10 transition-colors duration-200"
            >
              Disable for this site
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleDisableGlobally}
              className="text-prayer-primary hover:bg-gold/10 transition-colors duration-200"
            >
              Disable globally
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => window.location.href = '/settings'}
              className="text-prayer-primary/60 hover:bg-gold/10 transition-colors duration-200 text-xs"
            >
              You can re-enable in Settings
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
